import React, { useState } from "react";
import "../styles/ContactForm.scss";
import { useMutation } from "@tanstack/react-query";
import { config } from "../config";
import { metaPixelEvents } from "../utils/metaPixel";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCommentDots,
  FaPaperPlane,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const ContactForm = () => {
  const BASE_URL = config.BackendUrl;
  const [customerName, setCustomerName] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({});

  // 🔹 Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async (emailData) => {
      const response = await fetch(`${BASE_URL}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailData),
      });
      if (!response.ok) {
        throw new Error("Failed to send email");
      }
      return response.json();
    },
    onSuccess: (data) => {
      setResponseMessage("המייל נשלח בהצלחה! ניצור איתך קשר בהקדם");
      setTimeout(() => setResponseMessage(""), 5000);

      // Track successful lead submission
      metaPixelEvents.trackLead("contact_form");

      resetForm();
    },
    onError: () => {
      setResponseMessage("שליחת המייל נכשלה. אנא נסה שוב.");
    },
  });

  // 🔹 Validate form inputs
  const validateForm = () => {
    let newErrors = {};

    if (!customerName.trim()) newErrors.customerName = "נא להזין שם מלא";
    if (!userPhone.trim()) newErrors.userPhone = "נא להזין מספר טלפון";
    else if (!/^\d{10}$/.test(userPhone))
      newErrors.userPhone = "מספר טלפון לא תקין";

    if (!userEmail.trim()) newErrors.userEmail = "נא להזין כתובת אימייל";
    else if (!/\S+@\S+\.\S+/.test(userEmail))
      newErrors.userEmail = "כתובת אימייל לא תקינה";

    if (!emailMessage.trim()) newErrors.emailMessage = "נא להזין הודעה";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Reset form
  const resetForm = () => {
    setCustomerName("");
    setUserEmail("");
    setUserPhone("");
    setEmailMessage("");
    setErrors({});
  };

  // 🔹 Handle form submission
  const onSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Track form submission attempt
      metaPixelEvents.trackInitiateCheckout("contact_form");

      sendEmailMutation.mutate({
        customerName,
        emailMessage,
        userEmail,
        userPhone,
      });
    }
  };

  return (
    <div className="contact-section">
      <div className="form-container">
        <form onSubmit={onSubmit} className="email-form">
          <div className="form-grid">
            {/* Name Field */}
            <div className="input-group">
              <div
                className={`input-wrapper ${
                  errors.customerName ? "error" : ""
                } ${customerName ? "filled" : ""}`}
              >
                <FaUser className="input-icon" />
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="modern-input"
                />
                <label className="floating-label">שם מלא *</label>
              </div>
              {errors.customerName && (
                <div className="error-message">
                  <FaExclamationTriangle className="error-icon" />
                  <span>{errors.customerName}</span>
                </div>
              )}
            </div>

            {/* Phone Field */}
            <div className="input-group">
              <div
                className={`input-wrapper ${errors.userPhone ? "error" : ""} ${
                  userPhone ? "filled" : ""
                }`}
              >
                <FaPhone className="input-icon" />
                <input
                  type="tel"
                  value={userPhone}
                  onChange={(e) => setUserPhone(e.target.value)}
                  className="modern-input"
                />
                <label className="floating-label">מספר טלפון *</label>
              </div>
              {errors.userPhone && (
                <div className="error-message">
                  <FaExclamationTriangle className="error-icon" />
                  <span>{errors.userPhone}</span>
                </div>
              )}
            </div>

            {/* Email Field */}
            <div className="input-group full-width">
              <div
                className={`input-wrapper ${errors.userEmail ? "error" : ""} ${
                  userEmail ? "filled" : ""
                }`}
              >
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="modern-input"
                />
                <label className="floating-label">כתובת אימייל *</label>
              </div>
              {errors.userEmail && (
                <div className="error-message">
                  <FaExclamationTriangle className="error-icon" />
                  <span>{errors.userEmail}</span>
                </div>
              )}
            </div>

            {/* Message Field */}
            <div className="input-group full-width textarea-container">
              <div
                className={`input-wrapper textarea-wrapper ${
                  errors.emailMessage ? "error" : ""
                } ${emailMessage ? "filled" : ""}`}
              >
                <FaCommentDots className="input-icon" />
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  className="modern-textarea"
                  rows="4"
                />
                <label className="floating-label">איך נוכל לעזור לכם? *</label>
              </div>
              {errors.emailMessage && (
                <div className="error-message">
                  <FaExclamationTriangle className="error-icon" />
                  <span>{errors.emailMessage}</span>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="submit-section">
            <button
              type="submit"
              disabled={sendEmailMutation.isLoading}
              className={`submit-button ${
                sendEmailMutation.isLoading ? "loading" : ""
              }`}
            >
              <span className="button-content">
                {sendEmailMutation.isLoading ? (
                  <>
                    <div className="spinner"></div>
                    שולח הודעה...
                  </>
                ) : (
                  <>
                    שלח הודעה
                    <FaPaperPlane className="button-icon" />
                  </>
                )}
              </span>
            </button>
          </div>
        </form>

        {/* Response Message */}
        {responseMessage && (
          <div
            className={`response-message ${
              responseMessage.includes("בהצלחה") ? "success" : "error"
            }`}
          >
            {responseMessage.includes("בהצלחה") ? (
              <FaCheckCircle className="response-icon" />
            ) : (
              <FaExclamationTriangle className="response-icon" />
            )}
            <span>{responseMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
